/**
 * Helpers for the Top Subscribed IPOs page (/top-subscribed-ipos).
 *
 * GET /api/market/subscription/top returns GMP-board rows carrying the issue's
 * final or running demand (subscription_times), so the rows are mapped with the
 * GMP board mapper and then ranked by that multiple. Where the live
 * subscription feed knows the NSE symbol, the row links to its bid book.
 */
import { mapApiItemsToBoardRows } from './gmpBoard';
import { isSameCompany } from './smeIpos';

export const TOP_TABS = [
  { key: 'all', label: 'All Issues', test: () => true },
  { key: 'mainboard', label: 'Mainboard', test: (row) => row.board === 'Mainboard' },
  { key: 'sme', label: 'SME', test: (row) => row.board === 'SME' },
];

export const topTabByKey = (key) => TOP_TABS.find((tab) => tab.key === key) || TOP_TABS[0];

// Demand brackets used by the page's filter
export const DEMAND_BANDS = [
  { key: 'all', label: 'Any demand', test: () => true },
  { key: 'mega', label: '100x and above', test: (row) => row.times >= 100 },
  { key: 'strong', label: '10x and above', test: (row) => row.times >= 10 },
  { key: 'full', label: 'Fully subscribed (1x+)', test: (row) => row.times >= 1 },
  { key: 'under', label: 'Under 1x', test: (row) => row.times < 1 },
];

export const demandBandByKey = (key) => DEMAND_BANDS.find((band) => band.key === key) || DEMAND_BANDS[0];

export const demandTone = (times) => {
  if (times >= 100) return { label: 'Mega demand', badge: 'bg-[#006666] text-white border-[#006666]', bar: 'bg-[#006666]' };
  if (times >= 10) return { label: 'Heavily oversubscribed', badge: 'bg-emerald-50 text-emerald-700 border-emerald-200', bar: 'bg-emerald-500' };
  if (times >= 1) return { label: 'Fully subscribed', badge: 'bg-teal-50 text-[#006666] border-teal-200', bar: 'bg-[#14b8a6]' };
  return { label: 'Undersubscribed', badge: 'bg-rose-50 text-rose-600 border-rose-200', bar: 'bg-rose-400' };
};

export const formatTimes = (times) =>
  times === null || times === undefined ? '—' : `${times.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}x`;

const toNumber = (value) => {
  const text = String(value ?? '').replace(/[^\d.-]/g, '').trim();
  // An empty or missing figure is "no demand data", never zero demand
  if (!text) return null;
  const number = Number(text);
  return Number.isFinite(number) ? number : null;
};

/**
 * Ranked rows, most subscribed first. Issues with no demand figure are dropped:
 * this page is a ranking, so a row without a multiple has nothing to rank by.
 *
 * @param {object[]} items - GET /api/market/subscription/top -> data.items
 * @param {object[]} subscriptionRows - selectSubscriptionRows (+ SME), for NSE symbols
 */
export const mapTopSubscribedRows = (items = [], subscriptionRows = [], now = new Date()) => {
  // The board mapper drops empty entries, so filter first to keep both lists aligned
  const source = (Array.isArray(items) ? items : []).filter(Boolean);
  return mapApiItemsToBoardRows(source, now)
    .map((row, index) => {
      const times = toNumber(source[index]?.subscription_times) ?? row.subscription;
      const symbol = subscriptionRows.find((entry) => entry.symbol && isSameCompany(entry.company, row.name))?.symbol || '';
      return {
        ...row,
        times,
        symbol,
        link: symbol ? `/ipo-subscription/${encodeURIComponent(symbol)}` : row.slug ? `/ipo-gmp/${row.slug}` : null,
      };
    })
    .filter((row) => typeof row.times === 'number')
    .sort((a, b) => b.times - a.times || a.name.localeCompare(b.name))
    .map((row, index) => ({ ...row, rank: index + 1 }));
};

export const filterTopRows = (rows, { tab = 'all', band = 'all', query = '' } = {}) => {
  const needle = query.trim().toLowerCase();
  return rows
    .filter(topTabByKey(tab).test)
    .filter(demandBandByKey(band).test)
    .filter((row) => !needle || [row.name, row.exchange, row.statusLabel].filter(Boolean).join(' ').toLowerCase().includes(needle));
};

const LIVE = ['open', 'closing_today'];

export const computeTopStats = (rows) => {
  const sme = rows.filter((row) => row.board === 'SME').length;
  return {
    total: rows.length,
    top: rows[0] ? { name: rows[0].name, times: rows[0].times, board: rows[0].board } : null,
    above10: rows.filter((row) => row.times >= 10).length,
    fullySubscribed: rows.filter((row) => row.times >= 1).length,
    live: rows.filter((row) => LIVE.includes(row.status)).length,
    sme,
    mainboard: rows.length - sme,
  };
};

// Bar widths on a log scale, so a 300x issue doesn't flatten every other row
export const demandBarWidth = (times, max) => {
  if (!(times > 0) || !(max > 0)) return 0;
  return Math.max(2, Math.round((Math.log10(1 + times) / Math.log10(1 + max)) * 100));
};

const csvCell = (value) => {
  if (value === null || value === undefined) return '';
  const text = String(value);
  return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

export const buildTopSubscribedCsv = (rows) => {
  const header = [
    'Rank', 'Company', 'Board', 'Exchange', 'Status', 'Times Subscribed', 'Price (INR)', 'Issue Size (INR Cr)',
    'Lot Size', 'GMP (INR)', 'GMP %', 'Estimated Listing (INR)', 'Open Date', 'Close Date', 'Listing Date',
  ];
  const lines = rows.map((row) =>
    [
      row.rank,
      row.name,
      row.board,
      row.exchange,
      row.statusLabel,
      row.times,
      row.price,
      row.sizeCr,
      row.lotSize,
      row.hasGmp ? row.gmp : null,
      row.gmpPercent === null ? null : Number(row.gmpPercent).toFixed(2),
      row.estListing,
      row.openOn,
      row.closeOn,
      row.listingOn,
    ]
      .map(csvCell)
      .join(',')
  );
  return [header.map(csvCell).join(','), ...lines].join('\r\n');
};
